import { ExclamationCircleOutlined, LeftOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Drawer,
  Modal,
  Select,
  Space,
  Spin,
  Tag,
  notification,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useGetMarketplaces } from '../../../../utils/api/marketplaces/useGetMarketplaces';
import { usePreviewOffersUpdate } from '../../../../utils/api/mpOffers/usePreviewOffersUpdate';
import { useUpdateOffers } from '../../../../utils/api/mpOffers/useUpdateOffers';
import { useGetStores } from '../../../../utils/api/stores/useGetStores';
import { PROMETHEE_BLUE } from '../../../../utils/const/colors';
import {
  buildOfferUpdateScope,
  OFFER_UPDATE_MAX_OFFER_IDS,
} from '../../../../utils/const/mpOffers';

const UPDATE_FIELDS = [
  { key: 'price', label: 'Prix' },
  { key: 'stock', label: 'Stock' },
];

const buildReportDescription = (report) => {
  const parts = [`${report?.submitted ?? 0} offre(s) envoyée(s) en diffusion`];

  if (report?.skipped_in_flight) {
    parts.push(`${report.skipped_in_flight} déjà en cours`);
  }
  if (report?.skipped_ignored) {
    parts.push(`${report.skipped_ignored} à ne pas publier`);
  }
  if (report?.failed) {
    parts.push(`${report.failed} en échec`);
  }

  return `${parts.join(' · ')}.`;
};

const MpOffersUpdateDrawer = ({
  open,
  onClose,
  selectedOffers = [],
  onUpdated = () => {},
}) => {
  const [selectedFields, setSelectedFields] = useState([]);
  const [targetMarketplace, setTargetMarketplace] = useState(null);
  const [targetStore, setTargetStore] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedFields([]);
      setTargetMarketplace(null);
      setTargetStore(null);
    }
  }, [open]);

  const { marketplaces, isLoading: isMarketplacesLoading } =
    useGetMarketplaces();
  const { stores, isLoading: isStoresLoading } = useGetStores({
    marketplace__id: targetMarketplace,
  });

  const handleMarketplaceChange = (value) => {
    setTargetMarketplace(value ?? null);
    setTargetStore(null);
  };

  const hasSelection = selectedOffers.length > 0;

  const tooManyOffers = selectedOffers.length > OFFER_UPDATE_MAX_OFFER_IDS;

  const allFieldsSelected = selectedFields.length === UPDATE_FIELDS.length;

  const scope = useMemo(
    () => buildOfferUpdateScope(selectedFields),
    [selectedFields]
  );

  const hasTarget =
    hasSelection || targetMarketplace !== null || targetStore !== null;

  const payload = useMemo(() => {
    if (scope === null) return null;

    if (hasSelection) {
      if (tooManyOffers) return null;
      return { scope, offer_ids: selectedOffers.map((offer) => offer.id) };
    }

    if (targetMarketplace === null && targetStore === null) return null;

    return {
      scope,
      ...(targetMarketplace !== null && { marketplace: targetMarketplace }),
      ...(targetStore !== null && { store: targetStore }),
    };
  }, [
    hasSelection,
    scope,
    selectedOffers,
    targetMarketplace,
    targetStore,
    tooManyOffers,
  ]);

  const {
    preview,
    isFetching: isPreviewLoading,
    isError: isPreviewError,
  } = usePreviewOffersUpdate(payload, { enabled: open && payload !== null });

  const canSubmit =
    hasTarget &&
    scope !== null &&
    !tooManyOffers &&
    !isPreviewLoading &&
    !isPreviewError &&
    preview != null;

  const { updateOffers, isLoading: isUpdating } = useUpdateOffers();

  const notifyReport = (report) => {
    const submitted = report?.submitted ?? 0;
    const failed = report?.failed ?? 0;
    const description = buildReportDescription(report);

    if (submitted > 0 && failed === 0) {
      notification.success({
        message: 'Mise à jour lancée',
        description,
        placement: 'bottomRight',
        duration: 5,
      });
      return;
    }

    notification.error({
      message: 'Échec de la mise à jour',
      description: report?.errors?.length
        ? `${description} ${report.errors.join(' — ')}`
        : description,
      placement: 'bottomRight',
      duration: 5,
    });
  };

  const handleSubmit = () => {
    Modal.confirm({
      title: 'Lancer la mise à jour',
      icon: <ExclamationCircleOutlined />,
      content: `${preview?.eligible ?? 0} offre(s) vont être envoyées en diffusion. Confirmer ?`,
      okText: 'Confirmer',
      cancelText: 'Annuler',

      onOk: async () => {
        try {
          const report = await updateOffers(payload);
          notifyReport(report);
          onUpdated();
          onClose();
        } catch (error) {
          notification.error({
            message: 'Erreur lors de la mise à jour',
            description:
              error?.response?.data?.detail ||
              error?.response?.data?.error ||
              "La mise à jour n'a pas pu être lancée.",
            placement: 'bottomRight',
            duration: 5,
          });
        }
      },
    });
  };

  const toggleAllFields = () => {
    setSelectedFields(
      allFieldsSelected ? [] : UPDATE_FIELDS.map((field) => field.key)
    );
  };

  const toggleField = (key) => {
    setSelectedFields((previous) =>
      previous.includes(key)
        ? previous.filter((fieldKey) => fieldKey !== key)
        : [...previous, key]
    );
  };

  const targetedMarketplaces = useMemo(() => {
    const marketplacesById = new Map();

    selectedOffers.forEach((offer) => {
      const marketplace = offer.store?.marketplace;
      if (!marketplace) return;

      const previous = marketplacesById.get(marketplace.id);
      marketplacesById.set(marketplace.id, {
        id: marketplace.id,
        name: marketplace.name,
        offersCount: (previous?.offersCount ?? 0) + 1,
      });
    });

    return Array.from(marketplacesById.values());
  }, [selectedOffers]);

  return (
    <Drawer
      closable={false}
      placement="right"
      onClose={onClose}
      open={open}
      width={400}
      title={
        <div>
          <Button
            type="link"
            icon={<LeftOutlined />}
            onClick={onClose}
            style={{ padding: 0 }}
          >
            Retour
          </Button>
          <div style={{ color: PROMETHEE_BLUE }}>Lancer une mise à jour</div>
        </div>
      }
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {hasSelection ? (
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              Marketplaces ciblées
            </div>
            <Space size={[8, 8]} wrap>
              {targetedMarketplaces.map((marketplace) => (
                <Tag key={marketplace.id}>
                  {marketplace.name} ({marketplace.offersCount})
                </Tag>
              ))}
            </Space>
            {tooManyOffers && (
              <Alert
                type="error"
                showIcon
                style={{ marginTop: 8 }}
                message={`${selectedOffers.length} offres sélectionnées : l'API en accepte ${OFFER_UPDATE_MAX_OFFER_IDS} au maximum. Réduisez la sélection, ou décochez tout pour cibler une marketplace entière.`}
              />
            )}
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              Marketplace à mettre à jour
            </div>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Select
                placeholder="Sélectionner une marketplace"
                style={{ width: '100%' }}
                value={targetMarketplace}
                onChange={handleMarketplaceChange}
                options={(marketplaces || []).map((marketplace) => ({
                  label: marketplace.name,
                  value: marketplace.id,
                }))}
                loading={isMarketplacesLoading}
                allowClear
              />
              <Select
                placeholder="Tous les stores de la marketplace"
                style={{ width: '100%' }}
                value={targetStore}
                onChange={(value) => setTargetStore(value ?? null)}
                options={(stores || []).map((store) => ({
                  label: store.name,
                  value: store.id,
                }))}
                loading={isStoresLoading}
                disabled={!targetMarketplace}
                allowClear
              />
            </Space>
          </div>
        )}

        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 8,
            }}
          >
            <span style={{ fontWeight: 600 }}>Lancer la mise à jour pour</span>
            <Button
              type="link"
              onClick={toggleAllFields}
              style={{
                padding: 0,
                height: 'auto',
                textDecoration: 'underline',
              }}
            >
              {allFieldsSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
            </Button>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            {UPDATE_FIELDS.map((field) => {
              const checked = selectedFields.includes(field.key);
              return (
                <Checkbox
                  key={field.key}
                  checked={checked}
                  onChange={() => toggleField(field.key)}
                  style={{
                    margin: 0,
                    padding: '8px 12px',
                    borderRadius: 6,
                    border: `1px solid ${checked ? PROMETHEE_BLUE : '#d9d9d9'}`,
                  }}
                >
                  {field.label}
                </Checkbox>
              );
            })}
          </div>
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            Résumé d&apos;impact
          </div>
          {payload === null ? (
            <span style={{ color: '#8c8c8c' }}>
              Choisissez une cible et au moins un champ.
            </span>
          ) : isPreviewError ? (
            <Alert
              type="error"
              showIcon
              message="Impossible de calculer l'impact."
            />
          ) : isPreviewLoading ? (
            <Spin size="small" />
          ) : (
            <span>
              <strong>{preview?.eligible ?? 0}</strong> offre(s) seront mises à
              jour
            </span>
          )}
        </div>

        <Button
          type="primary"
          block
          loading={isUpdating}
          disabled={!canSubmit}
          onClick={handleSubmit}
          style={
            canSubmit
              ? { backgroundColor: PROMETHEE_BLUE, color: '#fff' }
              : undefined
          }
        >
          Lancer la mise à jour
        </Button>
      </Space>
    </Drawer>
  );
};

export default MpOffersUpdateDrawer;
